import RuleGroup from "./RuleGroup";
import * as yup from "yup";
import { QUESTIONS_VALIDATION_SCHEMA } from "../Schemas/Questions";

export default class RuleSet {
    /**
     * @return {*}
     * @constructor
     */
    static get SCHEMA_VALIDATION() {
        return yup
            .object()
            .shape({
                ruleSetId: yup.number().required(),
                code: yup.string().required(),
                name: yup.string().required(),
                ruleGroupsSatisfier: yup
                    .string()
                    .oneOf(this.SUPPORTED_SATISFIERS)
                    .required(),
                ruleGroups: yup
                    .array()
                    .of(RuleGroup.SCHEMA_VALIDATION)
                    .required()
            })
            .required();
    }

    /**
     * Returns an array of supported satisfiers
     *
     * @return {[String]}
     * @constructor
     */
    static get SUPPORTED_SATISFIERS() {
        return [RuleGroup.SATISFIER_ALL, RuleGroup.SATISFIER_ANY, RuleGroup.SATISFIER_NONE];
    }
    /**
     * The "All" satisfier - all rule groups within this rule set must be valid for this rule set to be valid
     *
     * @return {String}
     * @constructor
     */
    static get SATISFIER_ALL() {
        return "All";
    }

    /**
     * The "Any" satisfier - at least one rule group within this rule set must be valid for this rule set to be valid
     *
     * @return {String}
     * @constructor
     */
    static get SATISFIER_ANY() {
        return "Any";
    }

    /**
     * The "None" satisfier - no rules groups within this rule set must be valid for this rule set to be valid
     *
     * @return {String}
     * @constructor
     */
    static get SATISFIER_NONE() {
        return "None";
    }

    /**
     * Initialises a Rule Set with a specific schema
     * @param {{ruleSetId: Number, code:String, name:String, ruleGroupsSatisfier:String, ruleGroups:[Object]}} schema
     */
    constructor(schema) {
        RuleSet.SCHEMA_VALIDATION.validateSync(schema);

        this._ruleSetId = schema.ruleSetId;
        this._code = schema.code;
        this._name = schema.name;
        this._ruleGroupsSatisfier = schema.ruleGroupsSatisfier;

        /**
         * @type {[RuleGroup]}
         * @private
         */
        this._ruleGroups = [];

        for (let ruleGroup of schema.ruleGroups) {
            this._ruleGroups.push(new RuleGroup(ruleGroup));
        }
    }

    /**
     * @return {{ruleGroupsSatisfier: String, ruleGroups: {name: String, rules: {comparison: String, comparisonValue: String, questionCode: String}[], ruleSetId: Number, rulesSatisfier: String}[], name: String}}
     */
    get schema() {
        return {
            ruleSetId: this._ruleSetId,
            code: this._code,
            name: this._name,
            ruleGroupsSatisfier: this._ruleGroupsSatisfier,
            ruleGroups: this._ruleGroups.map(ruleGroup => ruleGroup.schema)
        };
    }

    /**
     * Validates the current rule set against an array of answers
     *
     * @param {[{questionCode:String, answers:[{answer:String}]}]} questions
     * @return {{valid:Boolean, schema:Object, validRuleGroups:[Rule]}}
     */
    validate(questions) {
        QUESTIONS_VALIDATION_SCHEMA.validateSync(questions);

        const validRuleGroups = [];

        for (let ruleGroup of this._ruleGroups) {
            const result = ruleGroup.validate(questions);

            if (result.valid) {
                validRuleGroups.push(result);
            }
        }

        let valid = false;
        switch (this._ruleGroupsSatisfier) {
            case RuleSet.SATISFIER_ALL:
                valid = validRuleGroups.length === this._ruleGroups.length;
                break;
            case RuleSet.SATISFIER_ANY:
                valid = validRuleGroups.length > 0;
                break;
            case RuleSet.SATISFIER_NONE:
                valid = validRuleGroups.length === 0;
                break;
        }

        return {
            valid,
            schema: this.schema,
            validRuleGroups
        };
    }
}
